import "./styles.scss";

import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import { useApp } from "context/app";

import Button from "components/Button";
import AnimatedGradient from "components/AnimatedGradient";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as ArrowNotificationsIcon } from "assets/icons/new/card-notifications-arrow.svg";
import { ReactComponent as ErrorIcon } from "assets/icons/new/error.svg";

import useResponsive from "libs/helpers/useResponsive";
import getGradientPropsByStatus from "libs/helpers/getGradientPropsByStatus";

const mainClass = "details-card";

const DetailsCard = ({
  variantMobile,
  title,
  iconMobile,
  description,
  action,
  backAction,
  notifications,
  view,
  children,
  footer,
  no_border,
  no_overflow,
  largeHeaderMobileText,
  resetOnMobile,
  zIndex,
  onMouseLeave,
}) => {
  const { theme } = useApp();
  const { min, max } = useResponsive();

  const [notificationIndex, setNotificationIndex] = useState(0);

  const hasNotifications = notifications?.length > 0;
  const notification = notifications?.[notificationIndex];

  const paymentError = view === "payment_error";

  return (
    <div
      style={!!zIndex ? { zIndex } : {}}
      onMouseLeave={onMouseLeave || null}
      className={
        variantMobile && max.sm
          ? classnames(`${mainClass} ${mainClass}--${variantMobile}`)
          : classnames(mainClass, {
              [`${mainClass}--error`]: paymentError,
              [`${mainClass}--no_border`]: !!no_border,
              [`${mainClass}--no_overflow`]: !!no_overflow,
              [`${mainClass}--notifications`]: hasNotifications,
              [`${mainClass}--reset_on_mobile`]: !!resetOnMobile,
            })
      }
    >
      {title && (
        <div className={`${mainClass}__header`}>
          <div
            className={classnames(`${mainClass}__header__group`, {
              [`${mainClass}__header__group--has-icon-mobile`]: !!iconMobile,
            })}
          >
            {iconMobile && max.sm && (
              <div
                className={classnames(`${mainClass}__header__icon-mobile`, {
                  [`${mainClass}__header__icon-mobile--${notification?.status}`]:
                    !!notification?.status,
                })}
              >
                {iconMobile}
              </div>
            )}
            <div
              className={classnames(`${mainClass}__header__text`, {
                [`${mainClass}__header__text--dark`]:
                  notification?.status === 1,
              })}
            >
              <h3
                className={classnames({
                  "large-mobile-text": largeHeaderMobileText,
                })}
              >
                {backAction ? (
                  <button type="button" onClick={backAction}>
                    <i>
                      <ArrowIcon />
                    </i>
                    {title}
                  </button>
                ) : (
                  title
                )}
              </h3>
              {description && <span>{description}</span>}
            </div>
            {hasNotifications && notifications.length > 1 && (
              <div className={`${mainClass}__header__notifications-pager`}>
                <button
                  type="button"
                  onClick={() =>
                    setNotificationIndex((prevIndex) =>
                      prevIndex > 0 ? prevIndex - 1 : notifications.length - 1,
                    )
                  }
                >
                  {<ArrowNotificationsIcon />}
                </button>
                <span>
                  {notificationIndex + 1}/{notifications.length}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setNotificationIndex((prevIndex) =>
                      notifications.length - 1 > prevIndex ? prevIndex + 1 : 0,
                    )
                  }
                >
                  {<ArrowNotificationsIcon />}
                </button>
              </div>
            )}
            {action && (
              <div className={`${mainClass}__header__actions`}>
                {(Array.isArray(action) ? action : [action]).map(
                  (item, index) => (
                    <Action
                      key={index}
                      label={item?.label}
                      iconMobile={item?.iconMobile}
                      href={item?.href}
                      disabled={item?.loading}
                      isError={paymentError}
                      isStatic={item?.isStatic}
                      isDark={notification?.status === 1}
                      hasLightIcon={
                        hasNotifications && notification?.status !== 1
                      }
                      onClick={item?.onclick}
                    />
                  ),
                )}
              </div>
            )}
          </div>
          {hasNotifications && (
            <>
              <div
                className={classnames(`${mainClass}__header__notifications`, {
                  [`${mainClass}__header__notifications--actionless`]:
                    !notification?.action,
                  [`${mainClass}__header__notifications--dark`]:
                    notification?.status === 1,
                })}
              >
                <div>
                  <h3>{notification?.label ?? "Notification"}</h3>
                  {notification?.description && (
                    <p>{notification.description}</p>
                  )}
                  {notification?.action && (
                    <div className={`${mainClass}__header__notifications-cta`}>
                      <Button onClick={notification.action?.onclick}>
                        {notification.action?.label ?? "View"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div
                className={classnames(`${mainClass}__header__background`, {
                  [`${mainClass}__header__background--${notification?.status}`]:
                    !!notification?.status,
                })}
              >
                {min.sm && (
                  <AnimatedGradient
                    id="notifications-gradient"
                    {...getGradientPropsByStatus(notification?.status, theme)}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
      <div className={`${mainClass}__content`}>{children}</div>
      {footer && <div className={`${mainClass}__footer`}>{footer}</div>}
    </div>
  );
};

const Action = ({
  label,
  iconMobile,
  href,
  disabled,
  isStatic,
  isError,
  isDark,
  hasLightIcon,
  onClick,
}) => {
  const { t } = useTranslation();

  if (!href && typeof onClick !== "function" && !isStatic) return null;

  const Tag = isStatic
    ? "div"
    : typeof onClick === "function"
      ? "button"
      : Link;

  return (
    <Tag
      {...(!isStatic
        ? {
            className: classnames({
              disabled,
              [`${mainClass}__header__action--success`]: isDark,
              [`${mainClass}__header__action--light-icon`]: hasLightIcon,
            }),
            to: href,
            onClick,
          }
        : {})}
    >
      {isError && (
        <span>
          <ErrorIcon />
        </span>
      )}
      {label ?? t("Action")}
      {iconMobile && <i>{iconMobile}</i>}
    </Tag>
  );
};

export default DetailsCard;
